// src/modules/app-views/invoices/pages/Init/modals/PreviewNFCeModal/Step1.jsx
// *******************************************************************************
// PREVIEW NFCe STEP - DANFE NFC-e Preview
// *******************************************************************************
// Displays the DANFE preview for NFC-e invoice inside a modal.
// Reuses the existing section components from PreviewTab.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Divider, Typography, Alert } from 'antd';
import IssuerSection from '../../../Document/tabs/PreviewTab/IssuerSection';
import ItemsSection from '../../../Document/tabs/PreviewTab/ItemsSection';
import TotalsSection from '../../../Document/tabs/PreviewTab/TotalsSection';
import ConsumerSection from '../../../Document/tabs/PreviewTab/ConsumerSection';
import FiscalInfoSection from '../../../Document/tabs/PreviewTab/FiscalInfoSection';
import '../../../Document/tabs/PreviewTab/PreviewTab.less';

const { Title, Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PreviewNFCeStep = ({ data }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const status = data?.status;
  const isAuthorized = status === 'authorized';

  // Determine watermark text based on status
  const watermarkText = getWatermarkText(status);
  const showWatermark = !isAuthorized;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // Show message if no data
  if (!data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="warning"
          showIcon
          title="Dados da nota não disponíveis"
          description="Não foi possível carregar os dados da nota fiscal."
        />
      </div>
    );
  }

  return (
    <div className="preview-nfce-modal-content">
      {/* Status Alert */}
      {!isAuthorized && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            type={getAlertType(status)}
            showIcon
            title={getStatusTitle(status)}
            description={getStatusDescription(status)}
          />
        </div>
      )}

      {/* DANFE Preview Container - danfe-print-mode forces black text for print */}
      <div id="danfe-modal-print-container" className="danfe-container danfe-print-mode">
        {/* Watermark for non-authorized invoices */}
        {showWatermark && <div className="danfe-watermark">{watermarkText}</div>}

        {/* DANFE Content */}
        <div className="danfe-content">
          {/* Header - Document Type */}
          <div className="danfe-header">
            <Title level={5} style={{ margin: 0, textAlign: 'center' }}>
              DANFE NFC-e
            </Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 11 }}>
              Documento Auxiliar da Nota Fiscal de Consumidor Eletrônica
            </Text>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Section 1: Issuer Data */}
          <IssuerSection data={data} />

          <Divider dashed style={{ margin: '6px 0' }} />

          {/* Section 2: Items List */}
          <ItemsSection data={data} />

          <Divider dashed style={{ margin: '6px 0' }} />

          {/* Section 3: Totals and Payments */}
          <TotalsSection data={data} />

          <Divider dashed style={{ margin: '6px 0' }} />

          {/* Section 4: Consumer Data */}
          <ConsumerSection data={data} />

          <Divider dashed style={{ margin: '6px 0' }} />

          {/* Section 5: Fiscal Info, Access Key, QR Code */}
          <FiscalInfoSection data={data} />
        </div>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PreviewNFCeStep;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Get watermark text based on invoice status
 * @param {string} status - Invoice status
 * @returns {string} Watermark text
 */
function getWatermarkText(status) {
  const texts = {
    draft: 'RASCUNHO',
    pending: 'AGUARDANDO',
    rejected: 'REJEITADA',
    denied: 'DENEGADA',
    canceled: 'CANCELADA',
    validated: 'VALIDADA',
  };
  return texts[status] || 'RASCUNHO';
}

/**
 * Get alert type based on invoice status
 * @param {string} status - Invoice status
 * @returns {string} Alert type
 */
function getAlertType(status) {
  const types = {
    draft: 'info',
    pending: 'warning',
    rejected: 'error',
    denied: 'error',
    canceled: 'warning',
    validated: 'success',
  };
  return types[status] || 'info';
}

/**
 * Get status title for alert
 * @param {string} status - Invoice status
 * @returns {string} Status title
 */
function getStatusTitle(status) {
  const titles = {
    draft: 'Rascunho',
    pending: 'Aguardando SEFAZ',
    rejected: 'Rejeitada',
    denied: 'Denegada',
    canceled: 'Cancelada',
    validated: 'Validada',
  };
  return titles[status] || 'Rascunho';
}

/**
 * Get status description for alert
 * @param {string} status - Invoice status
 * @returns {string} Status description
 */
function getStatusDescription(status) {
  const descriptions = {
    draft: 'Este documento ainda não foi transmitido para a SEFAZ.',
    pending: 'Aguardando resposta da SEFAZ.',
    rejected: 'Documento rejeitado pela SEFAZ. Verifique os erros e corrija.',
    denied: 'Documento denegado pela SEFAZ.',
    canceled: 'Este documento foi cancelado.',
    validated: 'Documento validado e pronto para emissão.',
  };
  return descriptions[status] || '';
}
